export interface ArticleVideoEntry {
  url: string;
  label: string;
  thumbnail?: string;
}

interface RawMediaVideo {
  url?: string;
  label?: string;
  thumbnail?: string | null;
}

interface ArticleVideoSource {
  media_videos?: RawMediaVideo[] | null;
  video_urls?: string[] | null;
}

function normalizeForMatch(s: string): string {
  try {
    return decodeURIComponent(s).replace(/&amp;/g, '&').toLowerCase();
  } catch {
    return s.replace(/&amp;/g, '&').toLowerCase();
  }
}

function pathOnly(url: string): string {
  const noFragment = url.split('#')[0];
  const noQuery = noFragment.split('?')[0];
  return noQuery.replace(/\/+$/, '');
}

function labelFromUrl(url: string): string {
  try {
    const name = decodeURIComponent(pathOnly(url).split('/').pop() || '');
    const stripped = name.replace(/^[0-9a-f]{16,}[_\-]/i, '');
    return stripped.replace(/\.(mp4|webm|mov|ogg|m4v)$/i, '').trim() || 'Video';
  } catch {
    return 'Video';
  }
}

/**
 * Collect videos that should be appended below article content.
 *
 * Source-of-truth HTML is `content`. Each entry in the `media_videos` sidecar
 * (or legacy `video_urls` fallback) is kept only if its URL is NOT already
 * present in `content` — meaning the WYSIWYG will render it inline so we
 * should not duplicate it.
 */
export function collectAppendedArticleVideos(
  content: string | undefined | null,
  source: ArticleVideoSource | undefined | null,
): ArticleVideoEntry[] {
  const raw: RawMediaVideo[] =
    (source?.media_videos && source.media_videos.length
      ? source.media_videos
      : (source?.video_urls || []).map((u) => ({ url: u }))) || [];

  const haystack = normalizeForMatch(content || '');
  const seen = new Set<string>();
  const out: ArticleVideoEntry[] = [];

  for (const mv of raw) {
    if (!mv || !mv.url) continue;
    const url = mv.url;
    const dedupKey = normalizeForMatch(pathOnly(url));
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);

    if (haystack && haystack.includes(dedupKey)) continue;

    out.push({
      url,
      label: (mv.label && mv.label.trim()) || labelFromUrl(url),
      thumbnail: mv.thumbnail || undefined,
    });
  }

  return out;
}
